import React, { useEffect, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { validateCNPJ } from '../../../utils/cnpjValidator';
import { ICompanyData } from '../index';
import { TipoEmpresa } from '../../../stores/useInterfaceStore';
import api from '../../../services/api';
import { notify } from '../../../stores/useNotificationStore';
import SearchSelectField, { SearchOption } from '../../../components/SearchSelectField';
import MultiSearchSelectField from '../../../components/MultiSearchSelectField';
import { useT } from '../../../i18n';

interface DataTabProps {
  data: ICompanyData;
  setData: React.Dispatch<React.SetStateAction<ICompanyData>>;
  cnpjError: string;
  setCnpjError: React.Dispatch<React.SetStateAction<string>>;
  tipoEmpresaLogada: TipoEmpresa;
  empresaId: number | null;
}

interface CadastroAuxiliarItem {
  id: number;
  nome: string;
  nivel: number | null;
  ativo: boolean;
}

const DataTab: React.FC<DataTabProps> = ({ data, setData, cnpjError, setCnpjError, tipoEmpresaLogada, empresaId }) => {
  const t = useT();
  const [tiposEmpresa, setTiposEmpresa] = useState<CadastroAuxiliarItem[]>([]);
  const [ramosAtuacao, setRamosAtuacao] = useState<CadastroAuxiliarItem[]>([]);
  const [regulamentacoes, setRegulamentacoes] = useState<CadastroAuxiliarItem[]>([]);

  useEffect(() => {
    api.getCadastrosAuxiliares('tipo_empresa').then(res => {
      if (res.success && Array.isArray(res.data)) setTiposEmpresa(res.data as CadastroAuxiliarItem[]);
    });
    api.getCadastrosAuxiliares('ramo_atuacao').then(res => {
      if (res.success && Array.isArray(res.data)) setRamosAtuacao(res.data as CadastroAuxiliarItem[]);
    });
    api.getCadastrosAuxiliares('regulamentacao_setorial').then(res => {
      if (res.success && Array.isArray(res.data)) setRegulamentacoes(res.data as CadastroAuxiliarItem[]);
    });
  }, []);

  // Tipo de Empresa é a hierarquia ESTRUTURAL fixa (usada em permissões, menus,
  // licenciamento e numa CHECK constraint do banco) — por isso a lista é fechada a
  // estes 5 valores e não tem "+" (diferente de Ramo/Regulamentação/Tipo Endereço,
  // que são classificações livres). O Cadastro Auxiliar só define o nível de cada um.
  const TIPOS_EMPRESA_HIERARQUIA = ['4Comply', 'Consultoria', 'Grupo', 'Empresa Fim', 'Filial'];

  // Um tipo de empresa só pode criar empresas de nível hierárquico maior que o seu
  // (0 = mais alto). 4Comply não tem restrição.
  const meuTipo = tiposEmpresa.find(t => t.nome === tipoEmpresaLogada);
  const tiposDisponiveis = (tipoEmpresaLogada === '4Comply'
    ? tiposEmpresa.filter(t => t.ativo !== false)
    : tiposEmpresa.filter(t => t.ativo !== false && meuTipo && t.nivel !== null && meuTipo.nivel !== null && t.nivel > meuTipo.nivel)
  ).filter(t => TIPOS_EMPRESA_HIERARQUIA.includes(t.nome));

  const tipoOptions: SearchOption[] = tiposDisponiveis.map(t => ({ key: t.nome, label: t.nome }));
  const ramoOptions: SearchOption[] = ramosAtuacao.filter(r => r.ativo !== false).map(r => ({ key: r.nome, label: r.nome }));
  const regOptions: SearchOption[] = regulamentacoes.filter(r => r.ativo !== false).map(r => ({ key: r.nome, label: r.nome }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));

    if (name === 'cnpj') {
      if (validateCNPJ(value) || value === '') {
        setCnpjError('');
      } else {
        setCnpjError(t('companyRegistration.data.cnpjInvalidFeedback'));
      }
    }
  };

  // Cria um novo item de Cadastro Auxiliar direto do formulário (botão "+"). Tipo de
  // Empresa e Ramo/Regulamentação são globais (exigem 4Comply ou Consultoria no backend).
  const criarAuxiliar = async (
    tipo: string,
    nome: string,
    setOptions: React.Dispatch<React.SetStateAction<CadastroAuxiliarItem[]>>,
  ): Promise<SearchOption | null> => {
    try {
      const res = await api.createCadastroAuxiliar({ tipo, nome, empresa_id: empresaId ?? undefined });
      if (res.success && res.data) {
        setOptions(prev => [...prev, res.data as CadastroAuxiliarItem]);
        notify.success(t('companyRegistration.data.createdSuccess'));
        return { key: nome, label: nome };
      }
      notify.error((res.error as string) || t('companyRegistration.data.createError'));
      return null;
    } catch {
      notify.error(t('companyRegistration.data.connectionError'));
      return null;
    }
  };

  return (
    <Form>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="codigoId">
            <Form.Label>{t('companyRegistration.data.codigoId')}</Form.Label>
            <Form.Control type="text" value={data.id ? String(data.id) : t('companyRegistration.data.codigoIdPlaceholder')} disabled readOnly />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="tipo">
            <Form.Label>{t('companyRegistration.data.tipo')}</Form.Label>
            <SearchSelectField
              placeholder={t('companyRegistration.data.tipoPlaceholder')}
              options={tipoOptions}
              value={data.tipo}
              onSelect={opt => setData(prev => ({ ...prev, tipo: opt?.key || '' }))}
              disabled={tiposDisponiveis.length === 0 && !data.tipo}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="cnpj">
            <Form.Label>{t('companyRegistration.data.cnpj')}</Form.Label>
            <Form.Control type="text" name="cnpj" value={data.cnpj} onChange={handleChange} isInvalid={!!cnpjError} />
            <Form.Control.Feedback type="invalid">{cnpjError}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="razao_social">
            <Form.Label>{t('companyRegistration.data.razaoSocial')}</Form.Label>
            <Form.Control type="text" name="razao_social" value={data.razao_social} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="nome_fantasia">
            <Form.Label>{t('companyRegistration.data.nomeFantasia')}</Form.Label>
            <Form.Control type="text" name="nome_fantasia" value={data.nome_fantasia} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="inscricao_estadual">
            <Form.Label>{t('companyRegistration.data.inscricaoEstadual')}</Form.Label>
            <Form.Control type="text" name="inscricao_estadual" value={data.inscricao_estadual} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="inscricao_municipal">
            <Form.Label>{t('companyRegistration.data.inscricaoMunicipal')}</Form.Label>
            <Form.Control type="text" name="inscricao_municipal" value={data.inscricao_municipal} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="ramo_atuacao">
            <Form.Label>{t('companyRegistration.data.ramoAtuacao')}</Form.Label>
            <MultiSearchSelectField
              placeholder={t('companyRegistration.data.ramoAtuacaoPlaceholder')}
              options={ramoOptions}
              selected={data.ramo_atuacao}
              onChange={next => setData(prev => ({ ...prev, ramo_atuacao: next }))}
              onAdd={nome => criarAuxiliar('ramo_atuacao', nome, setRamosAtuacao)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="regulamentacao_setorial">
            <Form.Label>{t('companyRegistration.data.regulamentacao')}</Form.Label>
            <MultiSearchSelectField
              placeholder={t('companyRegistration.data.regulamentacaoPlaceholder')}
              options={regOptions}
              selected={data.regulamentacao_setorial}
              onChange={next => setData(prev => ({ ...prev, regulamentacao_setorial: next }))}
              onAdd={nome => criarAuxiliar('regulamentacao_setorial', nome, setRegulamentacoes)}
            />
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group controlId="dados_complementares">
            <Form.Label>{t('companyRegistration.data.dadosComplementares')}</Form.Label>
            <Form.Control as="textarea" rows={3} name="dados_complementares" value={data.dados_complementares} onChange={handleChange} />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default DataTab;
